import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form } from "formik";
import SubmitButton from "../../components/Forms/SubmitButton";
import { Link } from "react-router-dom";

export default function ReportSnagCodes() {
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [stats, setStats] = useState({ total: 0, with_snag_code: 0, without_snag_code: 0 });

    useEffect(() => {
        fetchSnagCodes();
    }, []);

    const fetchSnagCodes = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/snag_codes`);
            setUnits(response.data.units);
            setStats({
                total: response.data.total,
                with_snag_code: response.data.with_snag_code,
                without_snag_code: response.data.without_snag_code
            });
        } catch (error) {
            console.error("Failed to load snag codes:", error);
            alert("Failed to load snag codes report");
        } finally {
            setLoading(false);
        }
    };

    const filteredUnits = units.filter((unit) => {
        const search = searchTerm.toLowerCase();
        return (
            unit.buyer.toLowerCase().includes(search) ||
            (unit.cell && unit.cell.toLowerCase().includes(search)) ||
            (unit.buyer_type && unit.buyer_type.toLowerCase().includes(search)) ||
            (unit.booking_reference && unit.booking_reference.toLowerCase().includes(search)) ||
            unit.property_name.toLowerCase().includes(search) ||
            unit.unit_number.toLowerCase().includes(search) ||
            (unit.snag_code && unit.snag_code.toLowerCase().includes(search)) ||
            (unit.appointment_type && unit.appointment_type.toLowerCase().includes(search))
        );
    });

    const groupedByProperty = filteredUnits.reduce((acc, unit) => {
        if (!acc[unit.property_name]) {
            acc[unit.property_name] = [];
        }
        acc[unit.property_name].push(unit);
        return acc;
    }, {});

    return (
        <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Snag Codes Report</h1>
                <div className="flex gap-4 text-sm">
                    <div className="text-gray-600">
                        Total: <span className="font-bold">{stats.total}</span>
                    </div>
                    <div className="text-green-600">
                        With Code: <span className="font-bold">{stats.with_snag_code}</span>
                    </div>
                    <div className="text-red-600">
                        Without Code: <span className="font-bold">{stats.without_snag_code}</span>
                    </div>
                </div>
            </div>

            <div className="mb-4 flex gap-4 items-end">
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input
                        type="text"
                        placeholder="Search by buyer, cell, type, reference, property, unit, snag code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <Formik
                    initialValues={{}}
                    onSubmit={async (values, { setSubmitting }) => {
                        await axios({
                            method: "post",
                            url: `${process.env.REACT_APP_API_URL}reports/export_snag_codes`,
                            responseType: "blob",
                        })
                            .then((response) => {
                                const href = URL.createObjectURL(response.data);
                                const link = document.createElement("a");
                                link.href = href;
                                link.setAttribute("download", `snag-codes-report-${new Date().toISOString().split("T")[0]}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(href);
                            })
                            .catch((error) => {
                                console.error(error);
                                alert("Failed to export report");
                            });

                        setSubmitting(false);
                    }}
                >
                    {({ handleSubmit, isSubmitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitButton name="Export" disabled={isSubmitting} isSubmitting={isSubmitting} text="Download PDF" />
                        </Form>
                    )}
                </Formik>
            </div>

            {loading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p className="mt-2 text-gray-600">Loading snag codes...</p>
                </div>
            ) : filteredUnits.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                    {searchTerm ? "No units found matching your search." : "No units with snag codes found."}
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedByProperty).map(([propertyName, propertyUnits]) => (
                        <div key={propertyName} className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="bg-blue-600 text-white px-4 py-3 font-semibold">
                                {propertyName} ({propertyUnits.length} units)
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cell</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Number</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Snag Code</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Time</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {propertyUnits.map((unit, index) => (
                                            <tr key={index} className={`hover:bg-gray-50 ${!unit.has_snag_code ? 'bg-red-50' : ''}`}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{unit.buyer}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{unit.cell}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        unit.buyer_type === 'Proxy' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {unit.buyer_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{unit.booking_reference}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{unit.unit_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {unit.has_snag_code ? (
                                                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                            {unit.snag_code}
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                            Not Generated
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {unit.appointment_type || <span className="text-gray-400 italic">N/A</span>}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {unit.appointment_date || <span className="text-gray-400 italic">N/A</span>}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {unit.appointment_time || <span className="text-gray-400 italic">N/A</span>}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <Link to={`/booking/${unit.booking_id}`} className="text-blue-600 hover:text-blue-900 font-medium">
                                                        View Booking
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
